import React from 'react';
import { Shield, Scan, FileText, Download, AlertTriangle, Lightbulb, Sparkles, Route, Database } from 'lucide-react';
import { COPYRIGHT_TEXT } from '../utils/version';

export const MockDetectorInfo: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* What is MockDetector */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-blue-500" />
          <h3 className="font-semibold text-sm">What is MockDetector?</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          MockDetector scans your Lovable project for mock and test data that might accidentally end up in production. 
          It checks your code, pages, and database to help you catch hardcoded values, test emails, and placeholder data before you deploy.
        </p>
      </div>

      {/* Scan Types */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Scan className="h-4 w-4 text-green-500" />
          <h3 className="font-semibold text-sm">Scan Types</h3>
        </div>
        <div className="space-y-2">
          <div className="bg-orange-900/20 rounded p-2 space-y-1">
            <div className="flex items-center gap-2">
              <Scan className="h-3 w-3 text-orange-400" />
              <span className="text-xs font-semibold text-orange-300">Quick Scan This Page</span>
            </div>
            <p className="text-[10px] text-muted-foreground ml-5">
              Fast scan of the current page you're viewing - checks visible DOM elements for mock data
            </p>
          </div>
          
          <div className="bg-yellow-900/20 rounded p-2 space-y-1">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3 w-3 text-yellow-400" />
              <span className="text-xs font-semibold text-yellow-300">Smart Deep Scan</span>
            </div>
            <p className="text-[10px] text-muted-foreground ml-5">
              Analyzes source code files for hardcoded values and patterns - most thorough scan
            </p>
          </div>
          
          <div className="bg-red-900/20 rounded p-2 space-y-1">
            <div className="flex items-center gap-2">
              <Route className="h-3 w-3 text-red-400" />
              <span className="text-xs font-semibold text-red-300">Scan All Routes</span>
            </div>
            <p className="text-[10px] text-muted-foreground ml-5">
              Scans multiple predefined routes - good for comprehensive frontend checks
            </p>
          </div>
          
          <div className="bg-purple-900/20 rounded p-2 space-y-1">
            <div className="flex items-center gap-2">
              <Database className="h-3 w-3 text-purple-400" />
              <span className="text-xs font-semibold text-purple-300">Scan Database</span>
            </div>
            <p className="text-[10px] text-muted-foreground ml-5">
              Checks Supabase tables for test emails, mock phone numbers, placeholder data
            </p>
          </div>
        </div>
      </div>

      {/* Severity Levels */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-purple-500" />
          <h3 className="font-semibold text-sm">Severity Levels</h3>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-red-400">Critical</p>
              <p className="text-xs text-muted-foreground">Security risks - fix before production</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-orange-400">High</p>
              <p className="text-xs text-muted-foreground">Major issues - should be fixed soon</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-yellow-400">Medium</p>
              <p className="text-xs text-muted-foreground">Quality issues - review and fix</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-blue-400">Low</p>
              <p className="text-xs text-muted-foreground">Minor findings - fix when convenient</p>
            </div>
          </div>
        </div>
      </div>

      {/* Export & Reports */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-cyan-500" />
          <h3 className="font-semibold text-sm">Export & Reports</h3>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            After scanning, you can export detailed reports:
          </p>
          <ul className="space-y-1 ml-4">
            <li className="text-xs text-muted-foreground">
              • <span className="font-semibold text-foreground">📋 Copy Report</span> - Copy complete report to clipboard
            </li>
            <li className="text-xs text-muted-foreground">
              • <span className="font-semibold text-foreground">Download .txt</span> - Download full report as text file
            </li>
          </ul>
          <div className="bg-blue-900/20 rounded p-2 mt-2">
            <p className="text-[10px] text-blue-300">
              💡 Tip: Both formats are optimized for sharing with AI assistants like ChatGPT, Claude, or Cursor!
            </p>
          </div>
        </div>
      </div>

      {/* What Can MockDetector Find - Compressed for brevity */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <h3 className="font-semibold text-sm">What Can MockDetector Find?</h3>
        </div>
        
        {/* Critical Security */}
        <div className="bg-red-900/10 rounded p-2 space-y-1">
          <p className="text-xs font-semibold text-red-400 mb-1">🔥 Critical Security</p>
          <ul className="space-y-0.5 ml-3">
            <li className="text-[11px] text-muted-foreground">• Test credit cards, API keys, credentials</li>
            <li className="text-[11px] text-muted-foreground">• Hardcoded passwords and auth tokens</li>
          </ul>
        </div>

        {/* Development URLs */}
        <div className="bg-orange-900/10 rounded p-2 space-y-1">
          <p className="text-xs font-semibold text-orange-400 mb-1">🔧 Development Artifacts</p>
          <ul className="space-y-0.5 ml-3">
            <li className="text-[11px] text-muted-foreground">• Tunneling services, staging domains</li>
            <li className="text-[11px] text-muted-foreground">• Private IPs and development ports</li>
          </ul>
        </div>

        {/* Debug Code */}
        <div className="bg-yellow-900/10 rounded p-2 space-y-1">
          <p className="text-xs font-semibold text-yellow-400 mb-1">🐛 Debug Code</p>
          <ul className="space-y-0.5 ml-3">
            <li className="text-[11px] text-muted-foreground">• console.log statements, debugger</li>
            <li className="text-[11px] text-muted-foreground">• Debug flags and verbose modes</li>
          </ul>
        </div>

        {/* React Patterns */}
        <div className="bg-sky-900/10 rounded p-2 space-y-1">
          <p className="text-xs font-semibold text-sky-400 mb-1">⚛️ React Patterns</p>
          <ul className="space-y-0.5 ml-3">
            <li className="text-[11px] text-muted-foreground">• useState with hardcoded data</li>
            <li className="text-[11px] text-muted-foreground">• Test emails and Lorem Ipsum</li>
          </ul>
        </div>

        {/* Database */}
        <div className="bg-purple-900/10 rounded p-2 space-y-1">
          <p className="text-xs font-semibold text-purple-400 mb-1">🗄️ Database Mock Data</p>
          <ul className="space-y-0.5 ml-3">
            <li className="text-[11px] text-muted-foreground">• Sequential IDs and identical timestamps</li>
            <li className="text-[11px] text-muted-foreground">• Test emails and mock phone numbers</li>
          </ul>
        </div>

        <p className="text-[10px] text-muted-foreground italic mt-2">
          ...and 50+ more patterns! See full list in documentation.
        </p>
      </div>

      {/* Tips & Best Practices */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-yellow-500" />
          <h3 className="font-semibold text-sm">Tips & Best Practices</h3>
        </div>
        <ul className="space-y-1.5">
          <li className="text-xs text-muted-foreground">
            💡 Use <span className="font-semibold text-foreground">Quick Scan This Page</span> during development for fast feedback
          </li>
          <li className="text-xs text-muted-foreground">
            💡 Run <span className="font-semibold text-foreground">Smart Deep Scan</span> before major releases
          </li>
          <li className="text-xs text-muted-foreground">
            💡 Always <span className="font-semibold text-foreground">Scan Database</span> before deploying to production
          </li>
          <li className="text-xs text-muted-foreground">
            💡 Download reports to track progress and share with team
          </li>
        </ul>
      </div>

      {/* Version Info */}
      <div className="border-t border-muted/20 pt-3 mt-4">
        <p className="text-[10px] text-muted-foreground text-center">
          MockDetector - Built by Silver (Anton Fredriksson) 🇸🇪 with AI 🤖
        </p>
        <p className="text-[10px] text-muted-foreground text-center mt-1">
          {COPYRIGHT_TEXT}
        </p>
      </div>
    </div>
  );
};
